import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  analyticsAvailable,
  denyAnalyticsConsent,
  grantAnalyticsConsent,
} from '../services/analytics';
import { onConsentSettingsRequested, readConsent } from '../services/consent';

/*
 * The banner speaks the page's language: the landing's switch sets <html lang>, and
 * the banner follows it live. Other pages leave lang alone and get English.
 */
const TEXT = {
  en: {
    region: 'Cookie consent',
    body: 'May we use analytics cookies? They tell us which pages people visit so we can improve Chefty. We use PostHog, with data stored in the EU. Nothing is sent unless you accept, and you can change your mind any time under “Cookie settings”.',
    privacy: 'Privacy policy',
    privacyHref: '/privacy',
    current: (granted: boolean) => `Currently: ${granted ? 'accepted' : 'rejected'}.`,
    reject: 'Reject',
    accept: 'Accept',
  },
  de: {
    region: 'Cookie-Einwilligung',
    body: 'Dürfen wir Analyse-Cookies nutzen? Sie zeigen uns, welche Seiten besucht werden, damit wir Chefty verbessern können. Wir nutzen PostHog, die Daten liegen in der EU. Ohne dein Ja wird nichts gesendet, und du kannst deine Wahl jederzeit unter „Cookie-Einstellungen“ ändern.',
    privacy: 'Datenschutzerklärung',
    privacyHref: '/datenschutz',
    current: (granted: boolean) => `Aktuell: ${granted ? 'zugestimmt' : 'abgelehnt'}.`,
    reject: 'Ablehnen',
    accept: 'Zustimmen',
  },
};

function useDocumentLang(): keyof typeof TEXT {
  const read = () => (document.documentElement.lang === 'de' ? 'de' : 'en');
  const [lang, setLang] = useState<keyof typeof TEXT>(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setLang(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
    // The landing sets lang in a layout effect that runs before this one subscribes.
    setLang(read());
    return () => observer.disconnect();
  }, []);
  return lang;
}

/**
 * Asks once for analytics cookies, and again whenever "Cookie settings" is opened.
 * Accept and Reject are the same size and weight on purpose: a rejection that takes
 * more effort than consent is not a free choice under the GDPR.
 */
export function CookieBanner() {
  const [open, setOpen] = useState(() => analyticsAvailable && readConsent() === null);
  const current = readConsent();
  const t = TEXT[useDocumentLang()];

  useEffect(
    () =>
      onConsentSettingsRequested(() => {
        if (analyticsAvailable) setOpen(true);
      }),
    [],
  );

  if (!open) return null;

  return (
    <section className="consent" role="region" aria-label={t.region}>
      <p className="consent-text">
        {t.body}{' '}
        <Link to={t.privacyHref} onClick={() => setOpen(false)}>
          {t.privacy}
        </Link>
        {current && <span className="consent-current"> {t.current(current === 'granted')}</span>}
      </p>
      <div className="consent-actions">
        <button
          type="button"
          className="consent-btn"
          onClick={() => {
            setOpen(false);
            denyAnalyticsConsent();
          }}
        >
          {t.reject}
        </button>
        <button
          type="button"
          className="consent-btn"
          onClick={() => {
            setOpen(false);
            grantAnalyticsConsent();
          }}
        >
          {t.accept}
        </button>
      </div>
    </section>
  );
}
